import React from 'react';

const SOURCE_NAMES = { domain_import: 'Domain.com.au', rent_import: 'Rent.com.au' };

const PropertyDetailsSections = ({ property }) => {
  const src = property.source || (property.listing_source_name?.includes('Domain') ? 'domain_import' : 'rent_import');
  // Detect source from domain_url when listing_source_name is missing
  const sourceName = property.listing_source_name
    || SOURCE_NAMES[src]
    || (property.domain_url?.includes('domain.com') ? 'Domain.com.au'
      : property.domain_url?.includes('rent.com') ? 'Rent.com.au'
      : 'the listing source');

  return (
  <div className="details-sections">

    {/* ABOUT THIS PROPERTY — first */}
    {property.description ? (
      <div className="detail-section">
        <h2>About This Property</h2>
        <p className="description-text">{property.description}</p>
      </div>
    ) : (
      <div className="detail-section">
        <h2>About This Property</h2>
        <p style={{ color: '#6b7280', fontStyle: 'italic', lineHeight: '1.6' }}>
          Full property description is available on the original listing — use the <strong>View Full Listing &amp; Contact Agent</strong> button below.
        </p>
      </div>
    )}

    {property.amenities && property.amenities.length > 0 && (
      <div className="detail-section">
        <h2>Amenities</h2>
        <div className="amenities-grid">
          {property.amenities.map((amenity, index) => (
            <div key={amenity || index} className="amenity-item">
              <span className="amenity-check">&#10003;</span>
              <span>{amenity}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {property.house_rules && (
      <div className="detail-section">
        <h2>House Rules</h2>
        <p className="rules-text">{property.house_rules}</p>
      </div>
    )}

    {(property.has_pool || property.has_aircon || property.has_parking || property.has_outdoor_area || property.pet_friendly || property.num_living_areas || property.storeys > 1 || property.walkability_score || property.furnished) && (
      <div className="detail-section" data-testid="property-features">
        <h2>Property Features</h2>
        <div className="info-grid">
          {property.storeys > 1 && <div className="info-item"><span className="info-label">Storeys:</span> <span className="info-value">{property.storeys}</span></div>}
          {property.num_living_areas && <div className="info-item"><span className="info-label">Living Areas:</span> <span className="info-value">{property.num_living_areas}</span></div>}
          {property.parking_spaces && <div className="info-item"><span className="info-label">Parking Spaces:</span> <span className="info-value">{property.parking_spaces}</span></div>}
          {property.walkability_score > 0 && (
            <div className="info-item">
              <span className="info-label">Walkability:</span>
              <span className="info-value">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '80px', height: '8px', borderRadius: '4px', background: '#e5e7eb', display: 'inline-block', position: 'relative', overflow: 'hidden' }}>
                    <span style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${property.walkability_score}%`, borderRadius: '4px', background: property.walkability_score >= 70 ? '#059669' : property.walkability_score >= 40 ? '#d97706' : '#ef4444' }} />
                  </span>
                  {property.walkability_score}/100
                </span>
              </span>
            </div>
          )}
          {property.furnished && <div className="info-item"><span className="info-label">Furnished:</span> <span className="info-value" style={{ textTransform: 'capitalize' }}>{property.furnished}</span></div>}
        </div>
        <div className="amenities-grid" style={{ marginTop: '12px' }}>
          {property.has_pool && <div className="amenity-item"><span className="amenity-check">&#10003;</span> Swimming Pool</div>}
          {property.has_aircon && <div className="amenity-item"><span className="amenity-check">&#10003;</span> Air Conditioning</div>}
          {property.has_parking && <div className="amenity-item"><span className="amenity-check">&#10003;</span> Off-street Parking</div>}
          {property.has_outdoor_area && <div className="amenity-item"><span className="amenity-check">&#10003;</span> Outdoor Area / Deck</div>}
          {property.pet_friendly && <div className="amenity-item"><span className="amenity-check">&#10003;</span> Pet Friendly</div>}
        </div>
      </div>
    )}

    <div className="detail-section">
      <h2>Additional Information</h2>
      <div className="info-grid">
        <div className="info-item"><span className="info-label">Bedrooms:</span><span className="info-value">{property.total_bedrooms || 'TBA'}</span></div>
        <div className="info-item"><span className="info-label">Bathrooms:</span><span className="info-value">{property.total_bathrooms > 0 ? property.total_bathrooms : 'TBA'}</span></div>
        {property.bond_required > 0 && <div className="info-item"><span className="info-label">Bond Required:</span><span className="info-value">${property.bond_required}</span></div>}
        {property.pet_policy && <div className="info-item"><span className="info-label">Pet Policy:</span><span className="info-value">{property.pet_policy}</span></div>}
        {property.smoking_policy && <div className="info-item"><span className="info-label">Smoking Policy:</span><span className="info-value">{property.smoking_policy}</span></div>}
        {property.lease_term && <div className="info-item"><span className="info-label">Lease Term:</span><span className="info-value">{property.lease_term}</span></div>}
        {property.available_from && <div className="info-item"><span className="info-label">Available From:</span><span className="info-value">{new Date(property.available_from).toLocaleDateString()}</span></div>}
        {property.property_code && <div className="info-item"><span className="info-label">Property Code:</span><span className="info-value">{property.property_code}</span></div>}
        {property.address && property.address !== 'TBA' && <div className="info-item"><span className="info-label">Address:</span><span className="info-value">{property.address}</span></div>}
        {property.postcode && <div className="info-item"><span className="info-label">Postcode:</span><span className="info-value">{property.postcode}</span></div>}
      </div>
    </div>

    {/* HOW TO CONTACT THE LISTING AGENT — below description */}
    <div className="detail-section" style={{ background: '#1a2332', borderRadius: '12px', padding: '20px 24px' }}>
      <h2 style={{ color: 'white', marginBottom: '6px', fontSize: '20px' }}>How to Contact the Listing Agent</h2>
      <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', margin: '0 0 16px' }}>
        This property is listed on <strong style={{ color: 'white' }}>{sourceName}</strong>. Click the button below to open the full listing page where you will find the agent's name, phone number, email and inspection times.
      </p>

      {(property.agency_name || property.agency_logo) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', padding: '10px 14px', background: 'rgba(255,255,255,0.08)', borderRadius: '8px' }}>
          {property.agency_logo && (
            <img src={property.agency_logo} alt={property.agency_name}
              style={{ height: '36px', objectFit: 'contain', borderRadius: '4px', background: 'white', padding: '3px' }}
              onError={e => e.target.style.display = 'none'} />
          )}
          {property.agency_name && <div style={{ fontWeight: '700', fontSize: '15px', color: 'white' }}>{property.agency_name}</div>}
        </div>
      )}

      {property.domain_url ? (
        <a href={property.domain_url} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 28px', background: '#dc2626', color: 'white', borderRadius: '8px', fontWeight: '900', fontSize: '17px', textDecoration: 'none', marginBottom: '16px' }}
          data-testid="contact-agent-btn">
          View Full Listing &amp; Contact Agent →
        </a>
      ) : (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 28px', background: '#4b5563', color: 'rgba(255,255,255,0.75)', borderRadius: '8px', fontWeight: '700', fontSize: '15px', marginBottom: '16px', cursor: 'not-allowed' }}
          data-testid="contact-agent-unavailable">
          Listing link unavailable
        </div>
      )}

      <div style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(255,255,255,0.4)', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <span>Property ID: <strong style={{ color: 'rgba(255,255,255,0.6)' }}>{property.property_id}</strong></span>
        {property.listing_date && <span>Listed: <strong style={{ color: 'rgba(255,255,255,0.6)' }}>{new Date(property.listing_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></span>}
      </div>
    </div>
  </div>
);
};

export default PropertyDetailsSections;
